import React, { useState, useMemo } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Chip,
  Button,
  TextField,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  LinearProgress,
  Avatar,
  Stack,
  Alert,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  Visibility,
  ShoppingCart,
  Category as CategoryIcon,
  Refresh,
  PersonOutline,
  Download,
  WhatsApp,
  Phone,
  LocalFireDepartment,
  WarningAmber,
  AccessTime,
  FilterAlt,
  ShoppingBag,
  ArrowForward,
  Star,
  CheckCircle,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
} from 'recharts';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../components/ui/PageHeader';
import { BehaviorService } from '../services/behaviorService';

const COLORS = [
  '#2563EB', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6',
  '#EC4899', '#06B6D4', '#84CC16', '#F97316', '#6366F1'
];

type OrderDirection = 'asc' | 'desc';

const CustomerBehaviorDashboard: React.FC = () => {
  const navigate = useNavigate();
  
  // State for filters
  const [period, setPeriod] = useState<'24h' | '7d' | '30d' | '60d' | '90d' | '365d' | 'all'>('30d');
  const [customerId, setCustomerId] = useState<string>('');
  const [category, setCategory] = useState<string>('');
  const [activeTab, setActiveTab] = useState<number>(0);
  const [productMatrixFilter, setProductMatrixFilter] = useState<'all' | 'star' | 'high_interest_zero_cart' | 'cart_abandoned'>('all');
  
  // Sorting states
  const [catOrderBy, setCatOrderBy] = useState<string>('total_views');
  const [catOrder, setCatOrder] = useState<OrderDirection>('desc');

  const [prodOrderBy, setProdOrderBy] = useState<string>('view_count');
  const [prodOrder, setProdOrder] = useState<OrderDirection>('desc');

  const [leadOrderBy, setLeadOrderBy] = useState<string>('last_active');
  const [leadOrder, setLeadOrder] = useState<OrderDirection>('desc');

  const [rawOrderBy, setRawOrderBy] = useState<string>('view_timestamp');
  const [rawOrder, setRawOrder] = useState<OrderDirection>('desc');

  // State for raw logs pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  // 1. Fetch Consolidated Business Intelligence Insights
  const { 
    data: insightsData, 
    isLoading: loadingInsights, 
    isError: errorInsights, 
    refetch: refetchInsights 
  } = useQuery({
    queryKey: ['behavior', 'insights', period, category, customerId],
    queryFn: () => BehaviorService.getBehaviorInsights({
      period: period,
      category: category || undefined,
      customer_id: customerId || undefined,
    }),
    staleTime: 5 * 60 * 1000,
  });

  // 2. Fetch Raw Product Views Feed (for Audit tab)
  const { 
    data: productViewsData, 
    isLoading: loadingViews, 
    refetch: refetchViews 
  } = useQuery({
    queryKey: ['behavior', 'product-views', period, customerId, category, page, rowsPerPage],
    queryFn: () => BehaviorService.getProductViews({
      customer_id: customerId || undefined,
      category: category || undefined,
      period: period,
      limit: rowsPerPage,
      offset: page * rowsPerPage,
    }),
    staleTime: 5 * 60 * 1000,
  });

  const handleRefreshAll = () => {
    refetchInsights();
    refetchViews();
  };

  const handleExportProductViews = async () => {
    try {
      const blob = await BehaviorService.exportProductViews({
        customer_id: customerId || undefined,
        category: category || undefined,
        period: period,
      });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `product_views_${period}_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting product views:', error);
    }
  };

  const handleWhatsAppContact = (lead: any) => {
    const rawNumber = lead.whatsapp_no || lead.mobile_no || '';
    const cleanNumber = rawNumber.replace(/\D/g, '');
    const phoneWithCountry = cleanNumber.length === 10 ? `91${cleanNumber}` : cleanNumber;
    
    const topProd = lead.top_products?.[0] || 'our products';
    const topCat = lead.top_categories?.[0] || 'wholesale catalog';
    const message = encodeURIComponent(
      `Hi ${lead.customer_name}, greetings from Dhanshika Exports! We noticed your interest in our ${topProd} (${topCat} category). Would you like to see our latest wholesale pricing, bulk offers, or place an order?`
    );
    window.open(`https://wa.me/${phoneWithCountry}?text=${message}`, '_blank');
  };

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleString('en-IN', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-IN').format(value);
  };

  const kpis = insightsData?.summary_kpis;
  const categoryDemand = insightsData?.category_demand || [];
  const trafficHourly = insightsData?.traffic_hourly || [];
  const visitorSplit = insightsData?.visitor_split;
  const funnel = insightsData?.funnel || [];
  const productOpportunities = insightsData?.product_opportunities || [];
  const customerLeads = insightsData?.customer_leads || [];
  const rawViews = productViewsData?.views || [];
  const totalRawViews = productViewsData?.total || 0;

  // Sorted Category Demand
  const sortedCategories = useMemo(() => {
    return [...categoryDemand].sort((a: any, b: any) => {
      const aVal = a[catOrderBy];
      const bVal = b[catOrderBy];
      if (typeof aVal === 'string') {
        return catOrder === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      return catOrder === 'asc' ? ((aVal || 0) > (bVal || 0) ? 1 : -1) : ((aVal || 0) < (bVal || 0) ? 1 : -1);
    });
  }, [categoryDemand, catOrderBy, catOrder]);

  // Filter & Sorted Product Opportunities
  const sortedProducts = useMemo(() => {
    const filtered = productOpportunities.filter(p => {
      if (productMatrixFilter === 'all') return true;
      return p.opportunity_type === productMatrixFilter;
    });

    return [...filtered].sort((a: any, b: any) => {
      const aVal = a[prodOrderBy];
      const bVal = b[prodOrderBy];
      if (typeof aVal === 'string') {
        return prodOrder === 'asc' ? (aVal || '').localeCompare(bVal || '') : (bVal || '').localeCompare(aVal || '');
      }
      return prodOrder === 'asc' ? ((aVal || 0) > (bVal || 0) ? 1 : -1) : ((aVal || 0) < (bVal || 0) ? 1 : -1);
    });
  }, [productOpportunities, productMatrixFilter, prodOrderBy, prodOrder]);

  // Sorted Customer Leads (Default: Most recent active timestamp first)
  const sortedLeads = useMemo(() => {
    return [...customerLeads].sort((a: any, b: any) => {
      let aVal = a[leadOrderBy];
      let bVal = b[leadOrderBy];
      if (leadOrderBy === 'last_active') {
        aVal = a.last_active ? new Date(a.last_active).getTime() : 0;
        bVal = b.last_active ? new Date(b.last_active).getTime() : 0;
      }
      if (typeof aVal === 'string') {
        return leadOrder === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      return leadOrder === 'asc' ? ((aVal || 0) > (bVal || 0) ? 1 : -1) : ((aVal || 0) < (bVal || 0) ? 1 : -1);
    });
  }, [customerLeads, leadOrderBy, leadOrder]);

  // Sorted Raw Product Views Feed (Default: Most recent view timestamp first)
  const sortedRawViews = useMemo(() => {
    return [...rawViews].sort((a: any, b: any) => {
      let aVal = a[rawOrderBy];
      let bVal = b[rawOrderBy];
      if (rawOrderBy === 'view_timestamp') {
        aVal = a.view_timestamp ? new Date(a.view_timestamp).getTime() : 0;
        bVal = b.view_timestamp ? new Date(b.view_timestamp).getTime() : 0;
      }
      if (typeof aVal === 'string') {
        return rawOrder === 'asc' ? (aVal || '').localeCompare(bVal || '') : (bVal || '').localeCompare(aVal || '');
      }
      return rawOrder === 'asc' ? ((aVal || 0) > (bVal || 0) ? 1 : -1) : ((aVal || 0) < (bVal || 0) ? 1 : -1);
    });
  }, [rawViews, rawOrderBy, rawOrder]);

  // Sort request helpers
  const handleCatSort = (property: string) => {
    const isAsc = catOrderBy === property && catOrder === 'asc';
    setCatOrder(isAsc ? 'desc' : 'asc');
    setCatOrderBy(property);
  };

  const handleProdSort = (property: string) => {
    const isAsc = prodOrderBy === property && prodOrder === 'asc';
    setProdOrder(isAsc ? 'desc' : 'asc');
    setProdOrderBy(property);
  };

  const handleLeadSort = (property: string) => {
    const isAsc = leadOrderBy === property && leadOrder === 'asc';
    setLeadOrder(isAsc ? 'desc' : 'asc');
    setLeadOrderBy(property);
  };

  const handleRawSort = (property: string) => {
    const isAsc = rawOrderBy === property && rawOrder === 'asc';
    setRawOrder(isAsc ? 'desc' : 'asc');
    setRawOrderBy(property);
  };

  // Top 6 categories for chart display
  const topCategoriesChartData = categoryDemand.slice(0, 7).map(c => ({
    name: c.category_name.length > 15 ? `${c.category_name.slice(0, 15)}...` : c.category_name,
    fullName: c.category_name,
    views: c.total_views,
    cart_adds: c.cart_additions,
    conversion: c.conversion_rate,
  }));

  // Visitor split pie chart data
  const visitorPieData = visitorSplit ? [
    { name: 'Registered Buyers', value: visitorSplit.registered_views, color: '#10B981' },
    { name: 'Guest Shoppers', value: visitorSplit.guest_views, color: '#6366F1' },
  ].filter(d => d.value > 0) : [];

  if (loadingInsights && !insightsData) {
    return (
      <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="450px" gap={2}>
        <CircularProgress size={48} thickness={4} />
        <Typography variant="body1" color="text.secondary">
          Analyzing customer browsing patterns, category demand, and sales intent...
        </Typography>
      </Box>
    );
  }

  if (errorInsights) {
    return (
      <Box>
        <PageHeader
          title="Customer Behavior & Demand Intelligence"
          subtitle="Real-time traffic flow, category demand radar, product interest, and actionable sales leads"
        />
        <Paper sx={{ p: 4, textAlign: 'center', mt: 3 }}>
          <Typography variant="h6" color="error" gutterBottom>
            Failed to load customer analytics insights
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Please ensure your account has the required <strong>analytics.behavior.read</strong> permission.
          </Typography>
          <Button variant="outlined" onClick={handleRefreshAll} startIcon={<Refresh />}>
            Retry Analysis
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 6 }}>
      <PageHeader
        title="Customer Behavior & Demand Intelligence"
        subtitle="Executive insights on visitor traffic, high-demand categories, product opportunities, and warm sales leads"
      />

      {/* Control / Filter Bar */}
      <Paper elevation={0} sx={{ p: 2.5, mb: 3, border: '1px solid #E2E8F0', borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Time Period</InputLabel>
              <Select
                value={period}
                label="Time Period"
                onChange={(e) => {
                  setPeriod(e.target.value as any);
                  setPage(0);
                }}
              >
                <MenuItem value="24h">Last 24 Hours</MenuItem>
                <MenuItem value="7d">Last 7 Days</MenuItem>
                <MenuItem value="30d">Last 30 Days (Recommended)</MenuItem>
                <MenuItem value="60d">Last 60 Days</MenuItem>
                <MenuItem value="90d">Last 90 Days</MenuItem>
                <MenuItem value="365d">Last 1 Year</MenuItem>
                <MenuItem value="all">All Time (Full History)</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3.5}>
            <TextField
              fullWidth
              size="small"
              label="Filter by Category or Product"
              value={category}
              onChange={(e) => {
                setCategory(e.target.value);
                setPage(0);
              }}
              placeholder="e.g. Art & Craft, Slime, Stationery"
              InputProps={{
                endAdornment: category ? (
                  <Chip
                    label="Clear"
                    size="small"
                    onClick={() => setCategory('')}
                    sx={{ height: 22, cursor: 'pointer' }}
                  />
                ) : null,
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2.5}>
            <TextField
              fullWidth
              size="small"
              label="Customer ID (optional)"
              value={customerId}
              onChange={(e) => {
                setCustomerId(e.target.value);
                setPage(0);
              }}
              placeholder="Filter by UUID"
            />
          </Grid>

          <Grid item xs={6} sm={3} md={1.5}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<Refresh />}
              onClick={handleRefreshAll}
              sx={{ height: 40 }}
            >
              Refresh
            </Button>
          </Grid>

          <Grid item xs={6} sm={3} md={1.5}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              startIcon={<Download />}
              onClick={handleExportProductViews}
              sx={{ height: 40 }}
            >
              Export CSV
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* No Data Notice */}
      {(!kpis || kpis.total_views === 0) && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="subtitle2" fontWeight={600}>
            No visitor events recorded for the period &ldquo;{period}&rdquo;
          </Typography>
          <Typography variant="body2">
            Try switching to <strong>Last 90 Days</strong> or <strong>All Time</strong> to view historical customer traffic and purchasing intent patterns.
          </Typography>
        </Alert>
      )}

      {/* Executive Hero KPI Cards */}
      <Grid container spacing={2.5} mb={3.5}>
        {/* Top Category Card */}
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              height: '100%',
              borderRadius: 2,
              border: '1px solid #E2E8F0',
              boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
              position: 'relative',
              overflow: 'hidden',
              background: 'linear-gradient(135deg, #FFFFFF 0%, #F0FDF4 100%)',
            }}
          >
            <CardContent sx={{ p: 2.5 }}>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase">
                    Top Demand Category
                  </Typography>
                  <Typography variant="h5" fontWeight={700} color="success.dark" sx={{ my: 0.5, wordBreak: 'break-word' }}>
                    {kpis?.top_category_name || '—'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>{formatNumber(kpis?.top_category_views || 0)}</strong> hits ({kpis?.top_category_share || 0}% of all views)
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'success.light', color: 'success.dark', width: 44, height: 44 }}>
                  <CategoryIcon />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Top Product Eye-Catcher Card */}
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              height: '100%',
              borderRadius: 2,
              border: '1px solid #E2E8F0',
              boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
              background: 'linear-gradient(135deg, #FFFFFF 0%, #EFF6FF 100%)',
            }}
          >
            <CardContent sx={{ p: 2.5 }}>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box sx={{ pr: 1 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase">
                    Most Viewed Product
                  </Typography>
                  <Typography variant="h6" fontWeight={700} color="primary.main" sx={{ my: 0.5, fontSize: '1.05rem', lineHeight: 1.3 }}>
                    {kpis?.top_product_name ? (kpis.top_product_name.length > 28 ? `${kpis.top_product_name.slice(0, 28)}...` : kpis.top_product_name) : '—'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>{formatNumber(kpis?.top_product_views || 0)}</strong> product views
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'primary.light', color: 'primary.main', width: 44, height: 44, flexShrink: 0 }}>
                  <Visibility />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Purchase Intent Rate Card */}
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              height: '100%',
              borderRadius: 2,
              border: '1px solid #E2E8F0',
              boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
              background: 'linear-gradient(135deg, #FFFFFF 0%, #FFFBEB 100%)',
            }}
          >
            <CardContent sx={{ p: 2.5 }}>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase">
                    View-to-Cart Intent Rate
                  </Typography>
                  <Typography variant="h5" fontWeight={700} color="warning.dark" sx={{ my: 0.5 }}>
                    {kpis?.overall_intent_rate || 0}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>{formatNumber(kpis?.total_cart_adds || 0)}</strong> cart additions made
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'warning.light', color: 'warning.dark', width: 44, height: 44 }}>
                  <ShoppingCart />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Active Buyers & Reach Card */}
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              height: '100%',
              borderRadius: 2,
              border: '1px solid #E2E8F0',
              boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
              background: 'linear-gradient(135deg, #FFFFFF 0%, #FAF5FF 100%)',
            }}
          >
            <CardContent sx={{ p: 2.5 }}>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase">
                    Active Buyer Reach
                  </Typography>
                  <Typography variant="h5" fontWeight={700} color="secondary.main" sx={{ my: 0.5 }}>
                    {formatNumber(kpis?.unique_registered_customers || 0)} Registered
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    +{formatNumber(kpis?.unique_guest_sessions || 0)} guest shoppers
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: '#F3E8FF', color: 'secondary.main', width: 44, height: 44 }}>
                  <PersonOutline />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Modern Tab Navigation */}
      <Paper sx={{ mb: 3, borderRadius: 2, border: '1px solid #E2E8F0' }} elevation={0}>
        <Tabs
          value={activeTab}
          onChange={(_e, val) => setActiveTab(val)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            px: 2,
            borderBottom: '1px solid #E2E8F0',
            '& .MuiTab-root': {
              fontWeight: 600,
              fontSize: '0.95rem',
              py: 2,
              textTransform: 'none',
              gap: 1,
            },
          }}
        >
          <Tab icon={<TrendingUp fontSize="small" />} iconPosition="start" label="Executive Radar (Overview)" />
          <Tab icon={<CategoryIcon fontSize="small" />} iconPosition="start" label={`Category Demand (${categoryDemand.length})`} />
          <Tab icon={<Star fontSize="small" />} iconPosition="start" label={`Product Interest Radar (${productOpportunities.length})`} />
          <Tab icon={<LocalFireDepartment fontSize="small" sx={{ color: '#EF4444' }} />} iconPosition="start" label={`Hot Sales Leads (${customerLeads.length})`} />
          <Tab icon={<Visibility fontSize="small" />} iconPosition="start" label={`Raw Activity Feed (${formatNumber(totalRawViews)})`} />
        </Tabs>

        {/* Tab 1: Executive Radar & Overview */}
        {activeTab === 0 && (
          <Box p={3}>
            <Grid container spacing={3}>
              {/* Category Demand Hits Chart */}
              <Grid item xs={12} lg={7}>
                <Card variant="outlined" sx={{ height: '100%', borderRadius: 2 }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <Box>
                        <Typography variant="h6" fontWeight={700}>
                          Which Categories are Getting More Hits?
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Product views vs cart additions across top product lines
                        </Typography>
                      </Box>
                      <Chip label="Top Categories" size="small" color="primary" variant="outlined" />
                    </Box>

                    {topCategoriesChartData.length === 0 ? (
                      <Box display="flex" justifyContent="center" alignItems="center" height={280}>
                        <Typography color="text.secondary">No category data for this period</Typography>
                      </Box>
                    ) : (
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={topCategoriesChartData} margin={{ top: 10, right: 20, left: 0, bottom: 25 }}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                          <XAxis
                            dataKey="name"
                            angle={-20}
                            textAnchor="end"
                            height={60}
                            interval={0}
                            tick={{ fontSize: 11, fill: '#64748B' }}
                          />
                          <YAxis tick={{ fontSize: 11, fill: '#64748B' }} />
                          <RechartsTooltip
                            formatter={(value: any, name: string) => [
                              `${formatNumber(value)} ${name === 'views' ? 'views' : 'adds'}`,
                              name === 'views' ? 'Product Views' : 'Cart Additions'
                            ]}
                          />
                          <Legend wrapperStyle={{ paddingTop: 10 }} />
                          <Bar dataKey="views" fill="#2563EB" name="Product Views" radius={[4, 4, 0, 0]} />
                          <Bar dataKey="cart_adds" fill="#10B981" name="Cart Additions" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* Peak Visiting Hours Chart */}
              <Grid item xs={12} lg={5}>
                <Card variant="outlined" sx={{ height: '100%', borderRadius: 2 }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <Box>
                        <Typography variant="h6" fontWeight={700}>
                          When Are Customers Visiting?
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Hourly browsing traffic distribution (00:00 - 23:00 UTC)
                        </Typography>
                      </Box>
                      <AccessTime fontSize="small" sx={{ color: 'text.secondary' }} />
                    </Box>

                    {trafficHourly.length === 0 ? (
                      <Box display="flex" justifyContent="center" alignItems="center" height={280}>
                        <Typography color="text.secondary">No traffic data recorded</Typography>
                      </Box>
                    ) : (
                      <ResponsiveContainer width="100%" height={300}>
                        <AreaChart data={trafficHourly} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                          <defs>
                            <linearGradient id="colorTraffic" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.4}/>
                              <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0.0}/>
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                          <XAxis
                            dataKey="label"
                            interval={3}
                            tick={{ fontSize: 11, fill: '#64748B' }}
                          />
                          <YAxis tick={{ fontSize: 11, fill: '#64748B' }} />
                          <RechartsTooltip
                            formatter={(value: any) => [`${value} Views`, 'Visiting Traffic']}
                          />
                          <Area type="monotone" dataKey="views" stroke="#8B5CF6" strokeWidth={2} fillOpacity={1} fill="url(#colorTraffic)" />
                        </AreaChart>
                      </ResponsiveContainer>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* Buyer Conversion Funnel */}
              <Grid item xs={12} md={7}>
                <Card variant="outlined" sx={{ borderRadius: 2 }}>
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="h6" fontWeight={700} gutterBottom>
                      Buyer Conversion & Purchase Intent Funnel
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mb={3}>
                      How shoppers advance from discovery to cart and final checkout
                    </Typography>

                    <Stack spacing={2}>
                      {funnel.map((step, idx) => (
                        <Box key={step.stage}>
                          <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                            <Typography variant="body2" fontWeight={600}>
                              {idx + 1}. {step.stage}
                            </Typography>
                            <Typography variant="body2" fontWeight={700}>
                              {formatNumber(step.count)} events ({step.conversion_from_prev}% {idx > 0 ? 'conv.' : ''})
                            </Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={idx === 0 ? 100 : Math.min(100, Math.max(5, (step.count / (funnel[0]?.count || 1)) * 100))}
                            sx={{
                              height: 10,
                              borderRadius: 5,
                              bgcolor: '#F1F5F9',
                              '& .MuiLinearProgress-bar': {
                                bgcolor: idx === 0 ? '#2563EB' : idx === 1 ? '#10B981' : idx === 2 ? '#F59E0B' : '#8B5CF6',
                                borderRadius: 5,
                              }
                            }}
                          />
                        </Box>
                      ))}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>

              {/* Visitor Composition (Registered vs Guest) */}
              <Grid item xs={12} md={5}>
                <Card variant="outlined" sx={{ height: '100%', borderRadius: 2 }}>
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="h6" fontWeight={700} gutterBottom>
                      Visitor Composition
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                      Logged-in B2B accounts vs anonymous prospective buyers
                    </Typography>

                    {visitorPieData.length === 0 ? (
                      <Box display="flex" justifyContent="center" alignItems="center" height={200}>
                        <Typography color="text.secondary">No visitor data</Typography>
                      </Box>
                    ) : (
                      <Box display="flex" flexDirection="column" alignItems="center">
                        <ResponsiveContainer width="100%" height={180}>
                          <PieChart>
                            <Pie
                              data={visitorPieData}
                              dataKey="value"
                              nameKey="name"
                              innerRadius={45}
                              outerRadius={75}
                              paddingAngle={4}
                            >
                              {visitorPieData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.color} />
                              ))}
                            </Pie>
                            <RechartsTooltip />
                          </PieChart>
                        </ResponsiveContainer>
                        <Stack direction="row" spacing={3} mt={1}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#10B981' }} />
                            <Typography variant="caption" fontWeight={600}>
                              Registered ({visitorSplit?.registered_percentage || 0}%)
                            </Typography>
                          </Box>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#6366F1' }} />
                            <Typography variant="caption" fontWeight={600}>
                              Guest ({visitorSplit?.guest_percentage || 0}%)
                            </Typography>
                          </Box>
                        </Stack>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Tab 2: Category Demand Intelligence */}
        {activeTab === 1 && (
          <Box p={3}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2.5}>
              <Box>
                <Typography variant="h6" fontWeight={700}>
                  Category Demand Hierarchy & Market Share
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Complete ranking of which product categories attract the highest buyer interest and view-to-cart conversions
                </Typography>
              </Box>
              <Chip
                label={`${categoryDemand.length} Categories Analyzed`}
                color="primary"
                variant="outlined"
                size="small"
              />
            </Box>

            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#0F172A' }}>
                    <TableCell sx={{ color: '#ffffff', fontWeight: 600 }}>
                      <TableSortLabel
                        active={catOrderBy === 'category_name'}
                        direction={catOrderBy === 'category_name' ? catOrder : 'asc'}
                        onClick={() => handleCatSort('category_name')}
                        sx={{ color: '#fff !important', '& .MuiTableSortLabel-icon': { color: '#fff !important' } }}
                      >
                        Category Name
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ color: '#ffffff', fontWeight: 600 }} align="right">
                      <TableSortLabel
                        active={catOrderBy === 'total_views'}
                        direction={catOrderBy === 'total_views' ? catOrder : 'desc'}
                        onClick={() => handleCatSort('total_views')}
                        sx={{ color: '#fff !important', '& .MuiTableSortLabel-icon': { color: '#fff !important' } }}
                      >
                        Total Views (Hits)
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ color: '#ffffff', fontWeight: 600 }} align="center" width="220">
                      <TableSortLabel
                        active={catOrderBy === 'share_percentage'}
                        direction={catOrderBy === 'share_percentage' ? catOrder : 'desc'}
                        onClick={() => handleCatSort('share_percentage')}
                        sx={{ color: '#fff !important', '& .MuiTableSortLabel-icon': { color: '#fff !important' } }}
                      >
                        Market Share
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ color: '#ffffff', fontWeight: 600 }} align="right">
                      <TableSortLabel
                        active={catOrderBy === 'unique_customers'}
                        direction={catOrderBy === 'unique_customers' ? catOrder : 'desc'}
                        onClick={() => handleCatSort('unique_customers')}
                        sx={{ color: '#fff !important', '& .MuiTableSortLabel-icon': { color: '#fff !important' } }}
                      >
                        Unique Buyers
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ color: '#ffffff', fontWeight: 600 }} align="right">
                      <TableSortLabel
                        active={catOrderBy === 'cart_additions'}
                        direction={catOrderBy === 'cart_additions' ? catOrder : 'desc'}
                        onClick={() => handleCatSort('cart_additions')}
                        sx={{ color: '#fff !important', '& .MuiTableSortLabel-icon': { color: '#fff !important' } }}
                      >
                        Cart Additions
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ color: '#ffffff', fontWeight: 600 }} align="center">
                      <TableSortLabel
                        active={catOrderBy === 'conversion_rate'}
                        direction={catOrderBy === 'conversion_rate' ? catOrder : 'desc'}
                        onClick={() => handleCatSort('conversion_rate')}
                        sx={{ color: '#fff !important', '& .MuiTableSortLabel-icon': { color: '#fff !important' } }}
                      >
                        Conversion Rate
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ color: '#ffffff', fontWeight: 600 }} align="center">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedCategories.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Typography color="text.secondary" py={3}>
                          No category data found for the selected period
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    sortedCategories.map((cat, idx) => (
                      <TableRow key={cat.category_name} hover sx={{ '&:nth-of-type(even)': { bgcolor: '#F8FAFC' } }}>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1.5}>
                            <Typography variant="body2" fontWeight={700} color="text.secondary">
                              #{idx + 1}
                            </Typography>
                            <Typography variant="body2" fontWeight={600}>
                              {cat.category_name}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={700}>
                            {formatNumber(cat.total_views)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Box display="flex" alignItems="center" gap={1}>
                            <LinearProgress
                              variant="determinate"
                              value={cat.share_percentage}
                              sx={{
                                flexGrow: 1,
                                height: 8,
                                borderRadius: 4,
                                bgcolor: '#E2E8F0',
                                '& .MuiLinearProgress-bar': {
                                  bgcolor: COLORS[idx % COLORS.length],
                                  borderRadius: 4,
                                }
                              }}
                            />
                            <Typography variant="caption" fontWeight={700} sx={{ minWidth: 35 }}>
                              {cat.share_percentage}%
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">
                            {formatNumber(cat.unique_customers)} registered
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            ({formatNumber(cat.unique_sessions)} sessions)
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={600} color="success.main">
                            {formatNumber(cat.cart_additions)} adds
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={`${cat.conversion_rate.toFixed(1)}%`}
                            size="small"
                            sx={{
                              fontWeight: 700,
                              bgcolor: cat.conversion_rate >= 25 ? '#DCFCE7' : cat.conversion_rate >= 10 ? '#EFF6FF' : '#F1F5F9',
                              color: cat.conversion_rate >= 25 ? '#166534' : cat.conversion_rate >= 10 ? '#1E40AF' : '#475569',
                            }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<FilterAlt fontSize="small" />}
                            onClick={() => {
                              setCategory(cat.category_name);
                              setActiveTab(2); // Switch to Product Interest tab
                            }}
                          >
                            Drill Down
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Tab 3: Product Interest & Opportunities Radar */}
        {activeTab === 2 && (
          <Box p={3}>
            <Box display="flex" flexWrap="wrap" justifyContent="space-between" alignItems="center" gap={2} mb={3}>
              <Box>
                <Typography variant="h6" fontWeight={700}>
                  Product Interest & Actionable Opportunities
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Discover top high-demand champions, pricing barriers (high views, zero cart adds), and cart abandonment drop-offs
                </Typography>
              </Box>

              {/* Sub-Filters */}
              <Stack direction="row" spacing={1}>
                <Chip
                  label="All Products"
                  clickable
                  color={productMatrixFilter === 'all' ? 'primary' : 'default'}
                  onClick={() => setProductMatrixFilter('all')}
                />
                <Chip
                  icon={<Star sx={{ fontSize: '1rem !important' }} />}
                  label="High Demand Champions"
                  clickable
                  color={productMatrixFilter === 'star' ? 'success' : 'default'}
                  onClick={() => setProductMatrixFilter('star')}
                />
                <Chip
                  icon={<WarningAmber sx={{ fontSize: '1rem !important' }} />}
                  label="High Views / 0 Cart Adds"
                  clickable
                  color={productMatrixFilter === 'high_interest_zero_cart' ? 'warning' : 'default'}
                  onClick={() => setProductMatrixFilter('high_interest_zero_cart')}
                />
                <Chip
                  icon={<ShoppingCart sx={{ fontSize: '1rem !important' }} />}
                  label="Cart Drop-Offs"
                  clickable
                  color={productMatrixFilter === 'cart_abandoned' ? 'secondary' : 'default'}
                  onClick={() => setProductMatrixFilter('cart_abandoned')}
                />
              </Stack>
            </Box>

            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#0F172A' }}>
                    <TableCell sx={{ color: '#ffffff', fontWeight: 600 }}>
                      <TableSortLabel
                        active={prodOrderBy === 'product_name'}
                        direction={prodOrderBy === 'product_name' ? prodOrder : 'asc'}
                        onClick={() => handleProdSort('product_name')}
                        sx={{ color: '#fff !important', '& .MuiTableSortLabel-icon': { color: '#fff !important' } }}
                      >
                        Product Details
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ color: '#ffffff', fontWeight: 600 }}>
                      <TableSortLabel
                        active={prodOrderBy === 'category_name'}
                        direction={prodOrderBy === 'category_name' ? prodOrder : 'asc'}
                        onClick={() => handleProdSort('category_name')}
                        sx={{ color: '#fff !important', '& .MuiTableSortLabel-icon': { color: '#fff !important' } }}
                      >
                        Category
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ color: '#ffffff', fontWeight: 600 }} align="right">
                      <TableSortLabel
                        active={prodOrderBy === 'view_count'}
                        direction={prodOrderBy === 'view_count' ? prodOrder : 'desc'}
                        onClick={() => handleProdSort('view_count')}
                        sx={{ color: '#fff !important', '& .MuiTableSortLabel-icon': { color: '#fff !important' } }}
                      >
                        Views
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ color: '#ffffff', fontWeight: 600 }} align="right">
                      <TableSortLabel
                        active={prodOrderBy === 'unique_customers'}
                        direction={prodOrderBy === 'unique_customers' ? prodOrder : 'desc'}
                        onClick={() => handleProdSort('unique_customers')}
                        sx={{ color: '#fff !important', '& .MuiTableSortLabel-icon': { color: '#fff !important' } }}
                      >
                        Unique Buyers
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ color: '#ffffff', fontWeight: 600 }} align="right">
                      <TableSortLabel
                        active={prodOrderBy === 'cart_additions'}
                        direction={prodOrderBy === 'cart_additions' ? prodOrder : 'desc'}
                        onClick={() => handleProdSort('cart_additions')}
                        sx={{ color: '#fff !important', '& .MuiTableSortLabel-icon': { color: '#fff !important' } }}
                      >
                        Cart Additions
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ color: '#ffffff', fontWeight: 600 }} align="center">
                      <TableSortLabel
                        active={prodOrderBy === 'conversion_rate'}
                        direction={prodOrderBy === 'conversion_rate' ? prodOrder : 'desc'}
                        onClick={() => handleProdSort('conversion_rate')}
                        sx={{ color: '#fff !important', '& .MuiTableSortLabel-icon': { color: '#fff !important' } }}
                      >
                        Conversion %
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ color: '#ffffff', fontWeight: 600 }} align="center">Opportunity Insight</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedProducts.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Typography color="text.secondary" py={3}>
                          No products match this opportunity filter
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    sortedProducts.map((prod) => {
                      let badge = null;
                      if (prod.opportunity_type === 'star') {
                        badge = (
                          <Chip
                            icon={<Star fontSize="small" sx={{ color: '#166534 !important' }} />}
                            label="Top Champion"
                            size="small"
                            sx={{ bgcolor: '#DCFCE7', color: '#166534', fontWeight: 700 }}
                          />
                        );
                      } else if (prod.opportunity_type === 'high_interest_zero_cart') {
                        badge = (
                          <Tooltip title="High views but zero cart additions. Check price competitiveness or MOQ.">
                            <Chip
                              icon={<WarningAmber fontSize="small" sx={{ color: '#9A3412 !important' }} />}
                              label="Price/MOQ Barrier"
                              size="small"
                              sx={{ bgcolor: '#FFEDD5', color: '#9A3412', fontWeight: 700 }}
                            />
                          </Tooltip>
                        );
                      } else if (prod.opportunity_type === 'cart_abandoned') {
                        badge = (
                          <Chip
                            icon={<ShoppingCart fontSize="small" sx={{ color: '#6B21A8 !important' }} />}
                            label="High Cart Intent"
                            size="small"
                            sx={{ bgcolor: '#F3E8FF', color: '#6B21A8', fontWeight: 700 }}
                          />
                        );
                      } else {
                        badge = <Chip label="Standard" size="small" variant="outlined" />;
                      }

                      return (
                        <TableRow key={prod.product_id} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>
                              {prod.product_name}
                            </Typography>
                            {prod.product_sku && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                SKU: {prod.product_sku}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip label={prod.category_name || 'General'} size="small" variant="outlined" />
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight={700}>
                              {formatNumber(prod.view_count)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">
                              {formatNumber(prod.unique_customers)} buyers
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight={600} color={prod.cart_additions > 0 ? 'success.main' : 'text.secondary'}>
                              {formatNumber(prod.cart_additions)}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" fontWeight={700}>
                              {prod.conversion_rate.toFixed(1)}%
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            {badge}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Tab 4: Hot Sales Leads & Direct Outreach */}
        {activeTab === 3 && (
          <Box p={3}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2.5}>
              <Box>
                <Typography variant="h6" fontWeight={700} display="flex" alignItems="center" gap={1}>
                  <LocalFireDepartment sx={{ color: '#EF4444' }} />
                  Hot Customer Leads & Active Buyer Intent
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Real registered business buyers browsing your catalog. Sorted by most recent browsing activity so you can reach out immediately!
                </Typography>
              </Box>
              <Chip
                label={`${customerLeads.length} Active Leads`}
                color="error"
                size="small"
                sx={{ fontWeight: 700 }}
              />
            </Box>

            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#0F172A' }}>
                    <TableCell sx={{ color: '#ffffff', fontWeight: 600 }}>
                      <TableSortLabel
                        active={leadOrderBy === 'customer_name'}
                        direction={leadOrderBy === 'customer_name' ? leadOrder : 'asc'}
                        onClick={() => handleLeadSort('customer_name')}
                        sx={{ color: '#fff !important', '& .MuiTableSortLabel-icon': { color: '#fff !important' } }}
                      >
                        Buyer / Business Name
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ color: '#ffffff', fontWeight: 600 }}>What They Are Eyeing (Categories & Items)</TableCell>
                    <TableCell sx={{ color: '#ffffff', fontWeight: 600 }} align="center">
                      <TableSortLabel
                        active={leadOrderBy === 'view_count'}
                        direction={leadOrderBy === 'view_count' ? leadOrder : 'desc'}
                        onClick={() => handleLeadSort('view_count')}
                        sx={{ color: '#fff !important', '& .MuiTableSortLabel-icon': { color: '#fff !important' } }}
                      >
                        Browsing Activity
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ color: '#ffffff', fontWeight: 600 }} align="center">
                      <TableSortLabel
                        active={leadOrderBy === 'active_cart_item_count'}
                        direction={leadOrderBy === 'active_cart_item_count' ? leadOrder : 'desc'}
                        onClick={() => handleLeadSort('active_cart_item_count')}
                        sx={{ color: '#fff !important', '& .MuiTableSortLabel-icon': { color: '#fff !important' } }}
                      >
                        Cart Status
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ color: '#ffffff', fontWeight: 600 }}>
                      <TableSortLabel
                        active={leadOrderBy === 'last_active'}
                        direction={leadOrderBy === 'last_active' ? leadOrder : 'desc'}
                        onClick={() => handleLeadSort('last_active')}
                        sx={{ color: '#fff !important', '& .MuiTableSortLabel-icon': { color: '#fff !important' } }}
                      >
                        Last Active Time
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ color: '#ffffff', fontWeight: 600 }} align="center">Instant Sales Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedLeads.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography color="text.secondary" py={3}>
                          No registered customer browsing activity recorded for this period
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    sortedLeads.map((lead) => (
                      <TableRow key={lead.customer_id} hover>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1.5}>
                            <Avatar sx={{ bgcolor: '#DBEAFE', color: '#1E40AF', fontWeight: 700, width: 38, height: 38 }}>
                              {lead.customer_name.charAt(0).toUpperCase()}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" fontWeight={700}>
                                {lead.customer_name}
                              </Typography>
                              {lead.billing_name && lead.billing_name !== lead.customer_name && (
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {lead.billing_name}
                                </Typography>
                              )}
                              {lead.mobile_no && (
                                <Typography variant="caption" color="text.secondary" display="block">
                                  📱 {lead.mobile_no}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </TableCell>

                        <TableCell sx={{ maxWidth: 300 }}>
                          <Box display="flex" flexWrap="wrap" gap={0.5} mb={0.5}>
                            {lead.top_categories.map((cat: string) => (
                              <Chip
                                key={cat}
                                label={cat}
                                size="small"
                                color="primary"
                                variant="outlined"
                                sx={{ height: 20, fontSize: '0.7rem' }}
                              />
                            ))}
                          </Box>
                          <Typography variant="caption" color="text.secondary" sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                            Top: {lead.top_products.join(', ')}
                          </Typography>
                        </TableCell>

                        <TableCell align="center">
                          <Typography variant="body2" fontWeight={700}>
                            {formatNumber(lead.view_count)} views
                          </Typography>
                          <Typography variant="caption" color="success.main" fontWeight={600} display="block">
                            {lead.cart_adds_count} cart adds
                          </Typography>
                        </TableCell>

                        <TableCell align="center">
                          {lead.has_active_cart ? (
                            <Chip
                              icon={<ShoppingBag fontSize="small" sx={{ color: '#166534 !important' }} />}
                              label={`${lead.active_cart_item_count} in Cart`}
                              size="small"
                              sx={{ bgcolor: '#DCFCE7', color: '#166534', fontWeight: 700 }}
                            />
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              No active cart
                            </Typography>
                          )}
                        </TableCell>

                        <TableCell>
                          <Typography variant="body2" fontWeight={600} color="primary.main">
                            {formatDate(lead.last_active)}
                          </Typography>
                        </TableCell>

                        <TableCell align="center">
                          <Stack direction="row" spacing={1} justifyContent="center">
                            {(lead.whatsapp_no || lead.mobile_no) && (
                              <Tooltip title="Send WhatsApp Message with Browsed Item Context">
                                <IconButton
                                  size="small"
                                  sx={{ bgcolor: '#25D366', color: '#fff', '&:hover': { bgcolor: '#128C7E' } }}
                                  onClick={() => handleWhatsAppContact(lead)}
                                >
                                  <WhatsApp fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}

                            {lead.mobile_no && (
                              <Tooltip title={`Call ${lead.mobile_no}`}>
                                <IconButton
                                  size="small"
                                  sx={{ bgcolor: '#F1F5F9', color: '#334155', '&:hover': { bgcolor: '#E2E8F0' } }}
                                  onClick={() => window.open(`tel:${lead.mobile_no}`, '_self')}
                                >
                                  <Phone fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}

                            <Tooltip title="View Full Customer Profile">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => navigate(`/admin/parties/customers/${lead.customer_id}`)}
                              >
                                <ArrowForward fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Tab 5: Raw Activity Feed */}
        {activeTab === 4 && (
          <Box p={3}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Box>
                <Typography variant="h6" fontWeight={700}>
                  Product Views Event Feed ({formatNumber(totalRawViews)} total events)
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Complete audit log of individual product view interactions, sorted by most recent timestamp
                </Typography>
              </Box>
              {category && (
                <Chip
                  label={`Filtered: "${category}"`}
                  size="small"
                  onDelete={() => setCategory('')}
                  color="primary"
                  variant="outlined"
                />
              )}
            </Box>

            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#0F172A' }}>
                    <TableCell sx={{ color: '#ffffff', fontWeight: 600 }}>
                      <TableSortLabel
                        active={rawOrderBy === 'customer_name'}
                        direction={rawOrderBy === 'customer_name' ? rawOrder : 'asc'}
                        onClick={() => handleRawSort('customer_name')}
                        sx={{ color: '#fff !important', '& .MuiTableSortLabel-icon': { color: '#fff !important' } }}
                      >
                        Customer / Visitor
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ color: '#ffffff', fontWeight: 600 }}>
                      <TableSortLabel
                        active={rawOrderBy === 'product_name'}
                        direction={rawOrderBy === 'product_name' ? rawOrder : 'asc'}
                        onClick={() => handleRawSort('product_name')}
                        sx={{ color: '#fff !important', '& .MuiTableSortLabel-icon': { color: '#fff !important' } }}
                      >
                        Product
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ color: '#ffffff', fontWeight: 600 }}>
                      <TableSortLabel
                        active={rawOrderBy === 'category'}
                        direction={rawOrderBy === 'category' ? rawOrder : 'asc'}
                        onClick={() => handleRawSort('category')}
                        sx={{ color: '#fff !important', '& .MuiTableSortLabel-icon': { color: '#fff !important' } }}
                      >
                        Category
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ color: '#ffffff', fontWeight: 600 }}>
                      <TableSortLabel
                        active={rawOrderBy === 'view_timestamp'}
                        direction={rawOrderBy === 'view_timestamp' ? rawOrder : 'desc'}
                        onClick={() => handleRawSort('view_timestamp')}
                        sx={{ color: '#fff !important', '& .MuiTableSortLabel-icon': { color: '#fff !important' } }}
                      >
                        View Timestamp
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ color: '#ffffff', fontWeight: 600 }} align="right">
                      <TableSortLabel
                        active={rawOrderBy === 'view_duration_seconds'}
                        direction={rawOrderBy === 'view_duration_seconds' ? rawOrder : 'desc'}
                        onClick={() => handleRawSort('view_duration_seconds')}
                        sx={{ color: '#fff !important', '& .MuiTableSortLabel-icon': { color: '#fff !important' } }}
                      >
                        Duration
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ color: '#ffffff', fontWeight: 600 }} align="center">Profile</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedRawViews.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography color="text.secondary" py={3}>
                          No raw view events recorded for this period
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    sortedRawViews.map((view: any, idx: number) => (
                      <TableRow key={`${view.id || idx}-${view.view_timestamp}`} hover>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="body2" fontWeight={view.customer_id ? 600 : 400}>
                              {view.customer_name}
                            </Typography>
                            {!view.customer_id && (
                              <Chip label="Guest" size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
                            )}
                          </Box>
                          {view.customer_email && view.customer_email !== '—' && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {view.customer_email}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {view.product_name}
                          </Typography>
                          {view.product_sku && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              SKU: {view.product_sku}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip label={view.category || 'General'} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {formatDate(view.view_timestamp)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          {view.view_duration_seconds ? `${view.view_duration_seconds}s` : '—'}
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title={view.customer_id ? "View Customer Profile" : "Guest visitor"}>
                            <span>
                              <IconButton
                                size="small"
                                color="primary"
                                disabled={!view.customer_id}
                                onClick={() => view.customer_id && navigate(`/admin/parties/customers/${view.customer_id}`)}
                              >
                                <PersonOutline fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={[10, 20, 50, 100]}
              component="div"
              count={totalRawViews}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(_e, newP) => setPage(newP)}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
            />
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default CustomerBehaviorDashboard;


